/**
 * Single source of truth for nudge telemetry (apply-funnel recovery plan,
 * "Telemetry contract"). Every nudge surface (email, push, in-app) emits
 * through these helpers only, so the event names and property keys the daily
 * digest and the PostHog readouts query never drift.
 *
 *   nudge_shown       { nudge_id, channel, variant, job_id? }
 *   nudge_clicked     { nudge_id, channel, job_id? }
 *   nudge_suppressed  { nudge_id, reason, channel?, job_id? }
 *
 * `variant` is "test" or "holdout". Holdout users get `nudge_suppressed`
 * with reason "holdout" instead of the nudge: that event is the comparison arm.
 */

import { getCloudflareContext } from "@opennextjs/cloudflare";
import {
	capturePostHogEvent,
	type PostHogLikeEnv,
} from "@/lib/analytics/posthog-server";

export const NUDGE_IDS = [
	"return_finish",
	"abandoned_apply",
	"outcome_check_d7",
	"outcome_check_d14",
	"weekly_goal",
	"guest_save_sheet",
	"apply_moment_simplified",
] as const;
export type NudgeId = (typeof NUDGE_IDS)[number];

export const NUDGE_CHANNELS = ["in_app", "email", "push"] as const;
export type NudgeChannel = (typeof NUDGE_CHANNELS)[number];

export type NudgeVariant = "test" | "holdout";

export const NUDGE_SUPPRESS_REASONS = [
	"cap",
	"quiet_hours",
	"opt_out",
	"dedupe",
	"holdout",
] as const;
export type NudgeSuppressReason = (typeof NUDGE_SUPPRESS_REASONS)[number];

/** `notification_events.event_type` -> nudge id, for the email send/click
 * mirrors in the processor and the click redirector. Later nudge PRs add rows. */
export const NUDGE_EMAIL_EVENT_TYPES: Readonly<Record<string, NudgeId>> = {
	nudge_abandoned_apply: "abandoned_apply",
	// Default is the day-7 touch; the day-14 row carries payload `variant:"d14"`
	// (see resolveNudgeEmailId).
	nudge_outcome_check: "outcome_check_d7",
};

/** Nudge id for an email row. `nudge_outcome_check` rows carry the touch in
 * payload `variant` ("d7" | "d14"); everything else is a plain eventType lookup. */
export function resolveNudgeEmailId(
	eventType: string,
	variant?: string | null,
): NudgeId | null {
	if (eventType === "nudge_outcome_check" && variant === "d14") {
		return "outcome_check_d14";
	}
	return NUDGE_EMAIL_EVENT_TYPES[eventType] ?? null;
}

/** `variant` field of a notification payload, or null. */
export function payloadVariant(payloadJson?: string | null): string | null {
	if (!payloadJson) return null;
	try {
		const p: unknown = JSON.parse(payloadJson);
		if (typeof p !== "object" || p === null || !("variant" in p)) return null;
		return typeof p.variant === "string" ? p.variant : null;
	} catch {
		return null;
	}
}

export const NUDGE_EVENT_NAMES = {
	shown: "nudge_shown",
	clicked: "nudge_clicked",
	suppressed: "nudge_suppressed",
} as const;

export type NudgeEventName =
	(typeof NUDGE_EVENT_NAMES)[keyof typeof NUDGE_EVENT_NAMES];

export type NudgeEventInput =
	| {
			event: "nudge_shown";
			nudgeId: NudgeId;
			channel: NudgeChannel;
			variant: NudgeVariant;
			jobId?: string | null;
	  }
	| {
			event: "nudge_clicked";
			nudgeId: NudgeId;
			channel: NudgeChannel;
			jobId?: string | null;
	  }
	| {
			event: "nudge_suppressed";
			nudgeId: NudgeId;
			reason: NudgeSuppressReason;
			channel?: NudgeChannel;
			jobId?: string | null;
	  };

/** Pure: the exact `{event, properties}` pair that goes on the wire. */
export function buildNudgeEvent(input: NudgeEventInput): {
	event: NudgeEventName;
	properties: Record<string, unknown>;
} {
	const properties: Record<string, unknown> = { nudge_id: input.nudgeId };
	if (input.event === "nudge_suppressed") {
		properties.reason = input.reason;
		if (input.channel) properties.channel = input.channel;
	} else {
		properties.channel = input.channel;
		if (input.event === "nudge_shown") properties.variant = input.variant;
	}
	if (input.jobId) properties.job_id = input.jobId;
	return { event: input.event, properties };
}

/** PostHog env for server/cron captures; `{}` (capture no-ops) when there is
 * no Cloudflare context (tests, scripts). */
export async function nudgeCaptureEnv(): Promise<PostHogLikeEnv> {
	try {
		const { env } = await getCloudflareContext({ async: true });
		return env;
	} catch {
		return {};
	}
}

/** Server/cron capture. Awaited by callers (no floating promises on Workers). */
export async function captureNudgeServer(
	env: PostHogLikeEnv,
	input: NudgeEventInput & { userId: string },
): Promise<void> {
	const { event, properties } = buildNudgeEvent(input);
	try {
		await capturePostHogEvent(env, {
			distinctId: input.userId,
			event,
			properties,
		});
	} catch {
		// Telemetry must never break a send.
	}
}

type PostHogClientLike = {
	capture: (event: string, properties?: Record<string, unknown>) => unknown;
};

/** Client capture (in-app surfaces). Safe with a null/undefined client. */
export function captureNudgeClient(
	posthog: PostHogClientLike | null | undefined,
	input: NudgeEventInput,
): void {
	if (!posthog) return;
	const { event, properties } = buildNudgeEvent(input);
	posthog.capture(event, properties);
}
